import { createStoreEnhance, shallowCallback } from '@halo/zustand-enhance';
import { isSessionCategory, SessionCategoryPrefix } from '@halo/chat-sdk';
import { storeLogger } from '../logger';
import type { CreateSessionStateOptions, SessionStateStore } from './session';
import { sessionStoreCreator } from './session';
import type { CreateGroupOptions, GroupStore } from './group';
import { groupStoreCreator } from './group';
import type { CreateMessagesOptions, MessagesStore } from './messages';
import { messagesStoreCreator } from './messages';
import type { FileMsgsStore } from './fileMsgs';
import { fileMsgsStoreCreator } from './fileMsgs';
import { singleStoreCreator, type SingleStore } from './single';

export type SessionMainStore = {
  effects: Array<() => void> & Record<string, () => void>;
  destroySession: () => void;
};

export type SessionStore = SessionStateStore &
  SingleStore &
  MessagesStore &
  FileMsgsStore &
  GroupStore &
  SessionMainStore;

export type CreateSessionOptions = CreateSessionStateOptions &
  CreateMessagesOptions &
  CreateGroupOptions;

export const createSessionStore = (options: CreateSessionOptions) => {
  const useSessionStore = createStoreEnhance<SessionStore>((...args) => ({
    effects: [],
    ...sessionStoreCreator(...args),
    ...groupStoreCreator(...args),
    ...singleStoreCreator(...args),
    ...messagesStoreCreator(...args),
    ...fileMsgsStoreCreator(...args),
    destroySession: () => {
      const [, get] = args;
      const { effects, session, activated } = get();

      Object.values(effects).forEach((remove) => {
        remove();
      });
      console.log(`6. [halo-chat] destroySession.
session_id: ${session?.chat_id}
activated: ${activated}`);
    },
  }));

  // Init
  const { session } = options;

  const {
    createSessionState,
    createMessages,
    createFileMsgs,
    createGroup,
    initGroup,
    initSingle,
  } = useSessionStore.getState();

  const initList: Promise<void>[] = [];

  createSessionState(options);

  createMessages(options);

  createFileMsgs();

  if (isSessionCategory(session.chat_id, SessionCategoryPrefix.Group)) {
    createGroup(options);
    const p = initGroup();
    initList.push(p);
    // TODO amo: Refactor
    p.then(() => {
      useSessionStore.subscribe(
        (store) => store.activated,
        shallowCallback((activated) => {
          if (activated) {
            initGroup();
          }
        })
      );
    });
  } else if (isSessionCategory(session.chat_id, SessionCategoryPrefix.Single)) {
    const p = initSingle();
    initList.push(p);
  }

  Promise.allSettled(initList).then(() => {
    useSessionStore.setState({ ready: true });
  });

  useSessionStore.subscribe((store) => {
    storeLogger.log('sessionStore:' + session.name, store);
  });

  return useSessionStore;
};

export type SessionStoreInstance = ReturnType<typeof createSessionStore>;
